import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuthStore } from '@/stores/auth';
import { useNavigate } from 'react-router-dom';

const IDLE_TIMEOUT = 15 * 60 * 1000; // 15 minutes in milliseconds
const WARNING_BEFORE_LOGOUT = 60 * 1000; // Show warning 1 minute before logout

interface UseIdleTimeoutOptions {
    onIdle?: () => void;
    onWarning?: () => void;
    timeout?: number;
}

export function useIdleTimeout(options: UseIdleTimeoutOptions = {}) {
    const { timeout = IDLE_TIMEOUT } = options;
    const { isAuthenticated, logout, user } = useAuthStore();
    const navigate = useNavigate();

    const [showWarning, setShowWarning] = useState(false);
    const [remainingTime, setRemainingTime] = useState(60);

    const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const lastActivityRef = useRef<number>(Date.now());

    const handleLogout = useCallback(() => {
        setShowWarning(false);
        logout();
        navigate('/login', {
            state: {
                message: 'Phiên làm việc đã hết hạn do không hoạt động. Vui lòng đăng nhập lại.',
                type: 'session_expired'
            }
        });
    }, [logout, navigate]);

    const resetTimer = useCallback(() => {
        lastActivityRef.current = Date.now();

        // Clear existing timers
        if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
        }
        if (warningTimerRef.current) {
            clearTimeout(warningTimerRef.current);
        }
        if (countdownRef.current) {
            clearInterval(countdownRef.current);
        }

        // Hide warning if shown
        setShowWarning(false);
        setRemainingTime(60);

        if (!isAuthenticated) return;

        // Set warning timer (1 minute before timeout)
        warningTimerRef.current = setTimeout(() => {
            setShowWarning(true);
            setRemainingTime(60);

            // Start countdown
            countdownRef.current = setInterval(() => {
                setRemainingTime(prev => {
                    if (prev <= 1) {
                        if (countdownRef.current) {
                            clearInterval(countdownRef.current);
                        }
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            options.onWarning?.();
        }, timeout - WARNING_BEFORE_LOGOUT);

        // Set idle timer
        idleTimerRef.current = setTimeout(() => {
            options.onIdle?.();
            handleLogout();
        }, timeout);
    }, [isAuthenticated, timeout, handleLogout, options]);

    const extendSession = useCallback(() => {
        resetTimer();
    }, [resetTimer]);

    useEffect(() => {
        if (!isAuthenticated) {
            // Clear all timers when not authenticated
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
            if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
            if (countdownRef.current) clearInterval(countdownRef.current);
            setShowWarning(false);
            return;
        }

        // Activity events to track
        const events = [
            'mousedown',
            'mousemove',
            'keydown',
            'scroll',
            'touchstart',
            'click',
            'wheel',
        ];

        // Throttle the reset to avoid excessive calls
        let lastReset = Date.now();
        const throttledReset = () => {
            const now = Date.now();
            // Only reset if more than 5 seconds have passed since last reset
            if (now - lastReset > 5000) {
                lastReset = now;
                resetTimer();
            }
        };

        // Add event listeners
        events.forEach(event => {
            document.addEventListener(event, throttledReset, { passive: true });
        });

        // Initial timer setup
        resetTimer();

        // Cleanup
        return () => {
            events.forEach(event => {
                document.removeEventListener(event, throttledReset);
            });
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
            if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
            if (countdownRef.current) clearInterval(countdownRef.current);
        };
    }, [isAuthenticated, resetTimer]);

    // Check for stale session on mount and visibility change
    useEffect(() => {
        const checkSessionValidity = () => {
            if (!isAuthenticated) return;

            const timeSinceLastActivity = Date.now() - lastActivityRef.current;

            // If more than 15 minutes have passed (e.g., laptop was sleeping)
            if (timeSinceLastActivity > timeout) {
                handleLogout();
            }
        };

        // Check on visibility change (when tab becomes visible again)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkSessionValidity();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Also check on focus
        window.addEventListener('focus', checkSessionValidity);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', checkSessionValidity);
        };
    }, [isAuthenticated, timeout, handleLogout]);

    return {
        showWarning,
        remainingTime,
        extendSession,
        userName: user?.full_name || user?.email || 'User',
    };
}

// Warning Modal Component
export function IdleTimeoutWarning({
    show,
    remainingTime,
    onExtend,
    onLogout
}: {
    show: boolean;
    remainingTime: number;
    onExtend: () => void;
    onLogout: () => void;
}) {
    if (!show) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
                {/* Header */}
                <div className="bg-warning-500 px-6 py-4">
                    <div className="flex items-center gap-3 text-white">
                        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="font-bold text-lg">Phiên sắp hết hạn</h3>
                            <p className="text-white/80 text-sm">Do không hoạt động</p>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="p-6 text-center">
                    <div className="mb-6">
                        <div className="w-20 h-20 mx-auto bg-warning-100 dark:bg-warning-600/20 rounded-full flex items-center justify-center mb-4">
                            <span className="text-3xl font-bold text-warning-600">{remainingTime}</span>
                        </div>
                        <p className="text-neutral-600 dark:text-neutral-400">
                            Bạn sẽ tự động đăng xuất sau <strong className="text-warning-600">{remainingTime} giây</strong> do không hoạt động.
                        </p>
                    </div>

                    <p className="text-sm text-neutral-500 mb-6">
                        Nhấn "Tiếp tục làm việc" để giữ phiên đăng nhập.
                    </p>

                    <div className="flex gap-3">
                        <button
                            onClick={onLogout}
                            className="flex-1 px-4 py-3 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-xl hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors font-medium"
                        >
                            Đăng xuất
                        </button>
                        <button
                            onClick={onExtend}
                            className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium"
                        >
                            Tiếp tục làm việc
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
